import isRetryAllowed from 'is-retry-allowed'
import axios, { AxiosError, type AxiosResponse, type RawAxiosRequestHeaders } from 'axios'

const isNull = (value: unknown): value is null => value === null
const isEmpty = (value: unknown): boolean => value === null || value === undefined
const isNonEmptyString = (value: unknown): value is string => typeof value === 'string' && value.length > 0

const RETRY_COUNT = 1
const SAFE_HTTP_METHODS = ['get', 'head', 'options']
const IDEMPOTENT_HTTP_METHODS = SAFE_HTTP_METHODS.concat(['put', 'delete', 'post'])
const RETRY_ERROR_CODES = [408, 413, 429, 500, 502, 503, 504, 521, 522, 524]

const isRetryableError = (error: AxiosError): boolean => {
  try {
    if (error.code === 'ECONNABORTED' || !isNull(error.response)) {
      return false
    }

    const statusCode: number = (error?.response as any)?.status ?? 0
    const method: string = error?.config?.method ?? ''

    if (statusCode > 0) {
      return IDEMPOTENT_HTTP_METHODS.includes(method) && RETRY_ERROR_CODES.includes(statusCode)
    }

    return isNetworkError(error)
  } catch (_err) {
    return false
  }
}

const isNetworkError = (error: AxiosError): boolean => {
  const CODE_EXCLUDE_LIST = ['ERR_CANCELED', 'ECONNABORTED']
  if (isEmpty(error.response) || !isNonEmptyString(error.code)) {
    return false
  }

  // Prevents retrying timed out & cancelled requests
  if (CODE_EXCLUDE_LIST.includes(error.code)) {
    return false
  }
  // Prevents retrying unsafe errors
  return isRetryAllowed(error)
}

const retryCondition = (error: unknown): boolean => {
  if (!(error instanceof AxiosError)) {
    return false
  }
  return isRetryableError(error)
}

interface AxiosPostOptions {
  url: string
  payload: any
  headers?: RawAxiosRequestHeaders
  retryCount?: number
}

interface AxiosGetOptions {
  url: string
  headers?: RawAxiosRequestHeaders
  retryCount?: number
}

export const axiosPost = async<T>(options: AxiosPostOptions): Promise<T> => {
  let retryCounter = options?.retryCount ?? RETRY_COUNT
  do {
    try {
      const res: AxiosResponse<T> = await axios.post(options.url, options.payload, { headers: options.headers })
      return res.data
    } catch (err) {
      const isRetryableError = retryCondition(err)
      if (!isRetryableError || retryCounter <= 0) {
        throw err
      }
      retryCounter--
    }
  } while (retryCounter >= 0)

  return await Promise.reject(new Error('Unable to process the request'))
}

export const axiosGet = async<T>(options: AxiosGetOptions): Promise<T> => {
  let retryCounter = options?.retryCount ?? RETRY_COUNT
  do {
    try {
      const res: AxiosResponse<T> = await axios.get(options.url, { headers: options.headers })
      return res.data
    } catch (err) {
      const isRetryableError = retryCondition(err)
      if (!isRetryableError || retryCounter <= 0) {
        throw err
      }
      retryCounter--
    }
  } while (retryCounter >= 0)

  return await Promise.reject(new Error('Unable to process the request'))
}
